/**
 * `TwinRoute` — viewer route for `/t/:gtwinId` (WP-PP5 / RFC-108.1).
 *
 * Mount sequence:
 *   1. Read `gtwinId` from `useParams()`.
 *   2. If `#preset=<id>` is present in the URL hash, call
 *      `setActivePreset(id)` BEFORE building the Scene so the initial
 *      render reflects the chosen preset (no flash of combined).
 *   3. Call `gtwinClient.get(gtwinId)` to fetch the gTwin envelope.
 *   4. Build viewer `Layer[]` from `payload.geospatial.scene` + active
 *      preset, using `sceneItemToLayer` (which reuses the COG/TiTiler
 *      logic from `artifactToLayer.ts`).
 *   5. Hydrate the store: `setCatalog`, `setPresets`, `setLayerSlot`
 *      for every layer, and `setActivePreset` from payload default if no
 *      hash preset was specified.
 *   6. Set display CRS from `envelope.projectAnchor.displayCrs` (D1).
 *   7. Render `<WorkspaceShell />` with `<ScenePresetSwitcher />` in the
 *      header slot; or `<RouteScaffold>` on 404.
 *
 * D6: `engineeringModels` items are loaded into the store with
 * `visibility.engineeringPlaceholder = true`. The LayerPanel (not this
 * route) is responsible for the placeholder card — this component never
 * crashes on them.
 *
 * Token-only styling — no raw hex.
 */

import { useEffect, useState } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { TwinShell } from './TwinShell';
import { RouteScaffold } from './RouteScaffold';
import { ScenePresetSwitcher } from './ScenePresetSwitcher';
import { useViewerStore } from '@/state/useViewerStore';
import { DEFAULT_PRESETS } from '@/sdk-stub/gtwin-sdk';
import type { GTwinRecord, SceneSlot } from '@/sdk-stub/gtwin-sdk';
import type { ObjectIndex } from '@/sdk-stub/gtwin-sdk/scene-hierarchy-types';
import { NotFoundError } from '@/sdk-stub/gtwin-sdk';
import {
  buildSceneLayers as buildSceneLayersFromRecord,
  loadScenePackageRefs,
  type SceneReadinessNotice,
  type ScenePackageDocument,
  type SimulationReadinessReport,
  type UnifiedSceneTreeNode,
} from './scenePackage';
import { maybeInjectDefaultBasemap } from './defaultBasemap';
import { maybeInjectDefaultEnvironment } from '@/modules/weather';
import { tokens } from '@/design';

// Re-export the shared singleton (kept here for backwards-compat with
// any test that imports `gtwinClient` from TwinRoute). The actual
// instance lives in `gtwinClientSingleton.ts` so child components can
// reuse it without creating a circular import.
import { gtwinClient } from './gtwinClientSingleton';
export { gtwinClient };
import { collectSceneHierarchies } from '@/components/scene-tree/collectSceneHierarchies';

type LoadState =
  | { status: 'loading' }
  | {
      status: 'loaded';
      record: GTwinRecord;
      displayCrs: string;
      // proj4 of the anchor's displayCrs so the top-level render-frame path can
      // register the projected frame without a network lookup (V-CRS Option B).
      displayCrsProj4: string | null;
      tree: UnifiedSceneTreeNode;
      notices: SceneReadinessNotice[];
      readinessReport: SimulationReadinessReport | null;
      scenePackage: ScenePackageDocument | null;
      projectFrameCrs: string;
      projectScaleMode: 'visual' | 'engineering';
    }
  | { status: 'not-found' }
  | { status: 'error'; message: string };

export function TwinRoute() {
  const { gtwinId = '' } = useParams<{ gtwinId: string }>();
  const location = useLocation();
  const [loadState, setLoadState] = useState<LoadState>({ status: 'loading' });

  const setActivePreset = useViewerStore((s) => s.setActivePreset);
  const setPresets = useViewerStore((s) => s.setPresets);
  const setCatalog = useViewerStore((s) => s.setCatalog);
  const setLayerSlot = useViewerStore((s) => s.setLayerSlot);
  const setVisible = useViewerStore((s) => s.setVisible);

  useEffect(() => {
    if (!gtwinId) return;

    setLoadState({ status: 'loading' });

    // Step 2: apply hash preset BEFORE the async fetch so initial render
    // uses the correct preset (AC4 — no flash of combined)
    const hashPreset = parseHashPreset(location.hash);
    if (hashPreset) {
      setActivePreset(hashPreset);
    }

    let cancelled = false;

    void (async () => {
      try {
        const record = await gtwinClient.get(gtwinId);
        if (cancelled) return;

        // Determine display CRS per D1 and extract RFC-112 Phase E fields
        const anchor = record.envelope?.projectAnchor;
        const displayCrs =
          anchor && anchor.kind === 'projected' && anchor.displayCrs
            ? anchor.displayCrs
            : anchor && anchor.kind === 'projected' && anchor.horizontalCrs
            ? anchor.horizontalCrs
            : 'EPSG:4326';
        // V-CRS Option B: carry the anchor's proj4 so the top-level render-frame
        // path can register a projected display CRS without a network lookup
        // (the per-item path already receives proj4 via layer.displayCrsProj4).
        const displayCrsProj4 =
          anchor && anchor.kind === 'projected' ? anchor.displayCrsProj4 ?? null : null;
        
        // RFC-112 Phase E: extract frameCrs and scaleMode (defaults per ADR-012)
        const projectFrameCrs = (anchor as Record<string, unknown>)?.frameCrs as string || 'EPSG:3857';
        const projectScaleMode = ((anchor as Record<string, unknown>)?.scaleMode as 'visual' | 'engineering' | undefined) || 'visual';

        const geo = record.payload?.geospatial;
        const presets = geo?.scenePresets?.length
          ? geo.scenePresets
          : [...DEFAULT_PRESETS];

        // Set presets in store
        setPresets(presets);

        // If no hash preset was applied, use the payload default
        if (!hashPreset) {
          const defaultPresetId = geo?.activeScenePreset ?? 'scn-combined';
          setActivePreset(defaultPresetId);
        }

        // Build layers from scene-package/index refs when present, with
        // payload.geospatial.scene as the direct-slot fallback.
        const loadedRefs = await loadScenePackageRefs(record);
        if (cancelled) return;
        const built = buildSceneLayersFromRecord(record, loadedRefs);
        const { tree, notices, readinessReport } = built;
        // If the scene has no `basemaps` slot entries, inject an OSM
        // basemap as a real catalog layer so it (a) renders behind
        // everything and (b) appears as a togglable card in the layer
        // panel — letting the user hide the basemap to verify orthos.
        const withBasemap = maybeInjectDefaultBasemap(
          built.layers,
          built.slotEntries,
          built.visibilityEntries,
        );
        if (withBasemap.injected) {
          console.log('[TwinRoute] injected default OSM basemap (no basemap in scene)');
        }

        const withEnvironment = maybeInjectDefaultEnvironment(
          withBasemap.layers,
          withBasemap.slotEntries,
          withBasemap.visibilityEntries,
        );
        if (withEnvironment.injected) {
          console.log('[TwinRoute] injected default environment layers (12 effects)');
        }

        // Hydrate store (setCatalog handles visibleLayerIds init)
        setCatalog(withEnvironment.layers);

        // Register each layer's slot for selectVisibleLayers
        for (const { layerId, slot } of withEnvironment.slotEntries) {
          setLayerSlot(layerId, slot);
        }
        for (const { layerId, visible } of withEnvironment.visibilityEntries) {
          const layer = withEnvironment.layers.find((candidate) => candidate.id === layerId);
          // ADR-017: envelope `visible:true` must not override on-demand hidden
          // initial state from `setCatalog` — mount gate reads `visibleLayerIds`.
          const effectiveVisible =
            layer?.activationTier === "on-demand" ? false : visible;
          setVisible(layerId, effectiveVisible);
        }

        setLoadState({
          status: 'loaded',
          record,
          displayCrs,
          displayCrsProj4,
          tree,
          notices,
          readinessReport,
          scenePackage: loadedRefs.scenePackage,
          projectFrameCrs,
          projectScaleMode,
        });
      } catch (err) {
        if (cancelled) return;
        if (err instanceof NotFoundError) {
          setLoadState({ status: 'not-found' });
        } else {
          setLoadState({
            status: 'error',
            message: err instanceof Error ? err.message : String(err),
          });
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [gtwinId, location.hash, setActivePreset, setCatalog, setLayerSlot, setPresets, setVisible]);

  if (loadState.status === 'loading') {
    return (
      <div
        role="status"
        aria-live="polite"
        style={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: tokens.color.surface[0],
          color: tokens.color.ink.secondary,
          fontFamily: tokens.font.family.ui,
          fontSize: tokens.font.size.md,
        }}
        data-testid="twin-route-loading"
      >
        Loading project…
      </div>
    );
  }

  if (loadState.status === 'not-found') {
    return (
      <RouteScaffold
        gvwId="GVW-PP5-404"
        route={`/t/${gtwinId}`}
        title="Project not found"
        subhead="Project not found"
        body={`The project "${gtwinId}" could not be found. It may have been deleted or you may not have access.`}
        wpId="WP-PP5"
        data-testid="twin-route-not-found"
      />
    );
  }

  if (loadState.status === 'error') {
    return (
      <RouteScaffold
        gvwId="GVW-PP5-ERR"
        route={`/t/${gtwinId}`}
        title="Failed to load project"
        subhead="Load error"
        body={loadState.message}
        wpId="WP-PP5"
        data-testid="twin-route-error"
      />
    );
  }

  // status === 'loaded'
  const { record } = loadState;

  // G18: derive initialCamera / initialSavedViews from envelope.view when no
  // URL deep-link camera is present (RFC-102 deep-link wins).
  const hashHasView = location.hash.includes('view=') || location.hash.includes('viewro=');
  const envelopeView = record.envelope?.view;
  const rawCam = (!hashHasView && envelopeView?.camera) ? envelopeView.camera : null;
  const initialCamera =
    rawCam && (Math.abs(rawCam.lonLat[0]) >= 1e-6 || Math.abs(rawCam.lonLat[1]) >= 1e-6)
      ? rawCam
      : null;
  const initialSavedViews = envelopeView?.savedViews;

  const geo = record.payload?.geospatial;
  const presets = geo?.scenePresets?.length ? geo.scenePresets : [...DEFAULT_PRESETS];
  // INIT-2026-Q3-SCENE-HIERARCHY Block 3: extract embedded Object Index when present.
  // Cast is safe — the field is unknown on the type; the converter emits a conformant
  // ObjectIndex shape. Downstream (SceneTreeV2Container) validates the shape implicitly
  // via objectIndexToManifest. Absent for all non-indexed gTwins.
  const sceneObjectIndex = (geo?.objectIndex as ObjectIndex | undefined) ?? null;
  // INIT-2026-Q3-SCENE-HIERARCHY multi-layer V1: collect ALL per-layer
  // hierarchy references (one per CityGML theme). Back-compat: also provide
  // the single-url path for callers that still use it.
  const sceneHierarchies = sceneObjectIndex ? [] : collectSceneHierarchies(geo);
  const sceneHierarchyUrl = sceneObjectIndex ? null : (sceneHierarchies[0]?.hierarchyUrl ?? null);

  return (
    <div
      data-testid="twin-route"
      data-gtwin-id={gtwinId}
      style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}
    >
      <TwinRouteHeader gtwinId={gtwinId} presets={presets} />
      <TwinShell
        gtwinId={gtwinId}
        projectLabel={loadState.record.label ?? gtwinId}
        sceneTree={loadState.tree}
        sceneNotices={loadState.notices}
        readinessReport={loadState.readinessReport}
        scenePackage={loadState.scenePackage}
        hasExternalRefs={Boolean(
          (geo?.scene as unknown as Record<string, unknown> | undefined)?.packageRef
          || (geo?.scene as unknown as Record<string, unknown> | undefined)?.indexRef
          || (geo?.scene as unknown as Record<string, unknown> | undefined)?.scenePackageRef
        )}
        projectFrameCrs={loadState.projectFrameCrs}
        projectScaleMode={loadState.projectScaleMode}
        sceneObjectIndex={sceneObjectIndex}
        sceneHierarchyUrl={sceneHierarchyUrl}
        sceneHierarchies={sceneHierarchies}
        initialCamera={initialCamera}
        initialSavedViews={initialSavedViews}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Header sub-component — mounts ScenePresetSwitcher
// ---------------------------------------------------------------------------

interface TwinRouteHeaderProps {
  gtwinId: string;
  presets: GTwinRecord['payload']['geospatial']['scenePresets'];
}

function TwinRouteHeader({ gtwinId, presets }: TwinRouteHeaderProps) {
  const activePreset = useViewerStore((s) => s.activePreset);

  // The canonical preset switcher now lives inside `SceneTreePanel`
  // (compactPresetSwitcher slot, left rail). The fixed bottom-center header
  // duplicated that surface and overlapped the bottom status strip
  // ("Map only / iTwin / Combined" sat on top of cursor coords + CRS + FPS),
  // so the floating switcher is gated behind `?legacy-toolbar=1` and the
  // header now renders nothing in the modern layout.
  const isLegacy =
    typeof window !== 'undefined'
    && new URLSearchParams(window.location.search).has('legacy-toolbar');
  if (!isLegacy) return null;

  return (
    <header
      role="toolbar"
      aria-label="Scene preset switcher"
      style={{
        position: 'fixed',
        bottom: tokens.space[3],
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: tokens.zIndex.dock,
        display: 'flex',
        alignItems: 'center',
        gap: tokens.space[2],
        background: tokens.color.surface[1],
        borderRadius: tokens.radius.input,
        padding: `${tokens.space[2]}px ${tokens.space[3]}px`,
        boxShadow: tokens.elevation[2],
        border: tokens.border.thin,
      }}
    >
      <ScenePresetSwitcher
        gtwinId={gtwinId}
        presets={presets}
        active={activePreset}
      />
    </header>
  );
}

// ---------------------------------------------------------------------------
// Exported helpers for testing (AC1, AC4)
// ---------------------------------------------------------------------------

/** Extract layers + slot map from a gTwin record. Pure, no side effects. */
export function buildSceneLayers(record: GTwinRecord): {
  layers: ReturnType<typeof buildSceneLayersFromRecord>['layers'];
  slotEntries: Array<{ layerId: string; slot: SceneSlot }>;
  visibilityEntries: Array<{ layerId: string; visible: boolean }>;
} {
  const { layers, slotEntries, visibilityEntries } = buildSceneLayersFromRecord(record);
  return { layers, slotEntries, visibilityEntries };
}

/** Parse a `#preset=<id>` value from a hash string (exported for testing). */
export function parseHashPreset(hash: string): string | null {
  const raw = hash.startsWith('#') ? hash.slice(1) : hash;
  for (const part of raw.split('&')) {
    if (part.startsWith('preset=')) {
      const id = part.slice('preset='.length).trim();
      return id.length > 0 ? id : null;
    }
  }
  return null;
}
